import { useMemo } from 'react'

import cn from 'classnames'

import LoadingSpinner from '@src/components/icons/LoadingSpinner'
import { ResultCode, StepNames } from '@src/enums'
import { useApplicationStore } from '@src/zustand_stores'
import { useHistoryStore } from '@src/zustand_stores/history.store'
import { useOrderUpdatesBackground } from '@src/zustand_stores/orderUpdates.store'

interface PendingTransactionsButtonProps
    extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    className?: string
}

const PendingTransactionsButton = ({ className, ...props }: PendingTransactionsButtonProps) => {
    // Get pending transactions
    const { pendingOrderIds } = useOrderUpdatesBackground()
    const getOrder = useHistoryStore(state => state.getOrder)
    const openWalletDrawer = useApplicationStore(state => state.openWalletDrawer)

    // Get token information for each pending transaction
    const pendingTransactions = useMemo(() => {
        return pendingOrderIds
            .map(({ exchangeId, orderId }) => {
                const orderData = getOrder(exchangeId)

                // Skip transactions that have completed or timed out
                if (
                    orderData.currentStep === StepNames.MATCH_TIMED_OUT ||
                    orderData.result?.status === ResultCode.success
                ) {
                    return null
                }

                return {
                    exchangeId,
                    orderId,
                }
            })
            .filter(tx => tx !== null) // Filter out null entries (completed transactions)
    }, [pendingOrderIds, getOrder])

    // If no pending transactions, don't render anything
    if (pendingTransactions.length === 0) {
        return null
    }

    const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        // Open the wallet portfolio drawer
        openWalletDrawer(true)

        // Call the original onClick if provided
        if (props.onClick) {
            props.onClick(e)
        }
    }

    return (
        <div
            className={cn(
                'flex h-[50px] cursor-pointer items-center gap-2 rounded-2xl border border-primary/30 bg-[#1a1b23] px-4 shadow-sm transition-colors hover:bg-slate-800/70',
                className
            )}
            onClick={handleClick}
            {...props}
        >
            <LoadingSpinner className='h-5 w-5 animate-spin text-primary' />
            <span className='text-[15px] font-medium text-white'>
                {pendingTransactions.length} pending {pendingTransactions.length === 1 ? 'tx' : 'txs'}
            </span>
        </div>
    )
}

export default PendingTransactionsButton
